// The session the page holds (02-DESIGN/03-console/01-the-console.md §
// signing in: tokens stay in the browser; the console holds no session on
// any server). It lives in sessionStorage — one tab, one sign-in, gone when
// the tab closes — and is the one place a provider token rests. A sign-in
// under way is a different thing: it lives in localStorage, because the
// provider may return the browser in another tab — a registration whose
// verification mail opens its link in a new one — and the return must still
// find the state, the nonce and the verifier it began with (hq issue 018).

/** Exchange says how a code is traded and a token refreshed: in the page or through the same-origin endpoint. */
export interface Exchange {
  kind: "public" | "server-side";
  /** The provider's token endpoint, or the auth service's exchange endpoint at this origin. */
  url: string;
  /** The client id this surface was registered under; sent only on a public exchange. */
  clientId?: string;
}

/** Session is a signed-in person's tokens and where they came from. */
export interface Session {
  /** oidc | github */
  lane: "oidc" | "github";
  /** The tenant the sign-in named, or empty on the identity plane. */
  slug: string;
  /** The provider's issuer, for display and the check at refresh. */
  issuer: string;
  /** What the connect token carries: the ID token on the oidc lane, the access token on the github lane. */
  credential: string;
  refreshToken?: string;
  /** When the credential expires, in milliseconds since the epoch; absent when the provider did not say. */
  expiresAt?: number;
  /** The person's display, from the ID token's claims; never a key. */
  display?: string;
  exchange: Exchange;
}

/** Pending is a sign-in under way: what the page keeps between the redirect out and the return. */
export interface Pending {
  state: string;
  nonce: string;
  verifier: string;
  lane: "oidc" | "github";
  slug: string;
  issuer: string;
  exchange: Exchange;
  /** The address to return to after the flow. */
  returnTo: string;
  /** When it was begun, in milliseconds since the epoch; a stale one is refused. */
  begun: number;
  /**
   * probe marks a sign-in that proves a switch of the workspace's provider
   * rather than signing in: its token is handed to the switch, not connected
   * with (02-DESIGN/01-identity/03-signing-in.md § switching a workspace's
   * provider). `own`, or `inherited` for a switch back.
   */
  probe?: Probe;
}

/** Probe names the provider a proving sign-in is for. */
export type Probe = "own" | "inherited";

/** Proof is a proving sign-in completed: the provider it proves and the session it produced. */
export interface Proof {
  probe: Probe;
  session: Session;
}

const SESSION = "impire.session";
const PROOF = "impire.signin.proof";
const PENDING = "impire.signin.";

/** Store is a key-value store: sessionStorage for the session, localStorage for a sign-in under way, or a stand-in in tests. */
export interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function storage(): Store | undefined {
  try {
    return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

/** pendingStorage is where a sign-in under way rests: shared by every tab of the origin. */
function pendingStorage(): Store | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

function read(store: Store | undefined, key: string): unknown {
  try {
    const raw = store?.getItem(key);
    return raw === null || raw === undefined ? undefined : (JSON.parse(raw) as unknown);
  } catch {
    return undefined;
  }
}

function write(store: Store | undefined, key: string, value: unknown): void {
  try {
    store?.setItem(key, JSON.stringify(value));
  } catch {
    // A store that refuses a write leaves the page signed out at the next load; nothing else to do.
  }
}

/** loadSession reads the session, or nothing. */
export function loadSession(store: Store | undefined = storage()): Session | undefined {
  const s = read(store, SESSION) as Session | undefined;
  if (s === undefined || typeof s.credential !== "string" || s.credential === "") {
    return undefined;
  }
  return s;
}

/** saveSession keeps the session. */
export function saveSession(s: Session, store: Store | undefined = storage()): void {
  write(store, SESSION, s);
}

/** clearSession forgets the session: signing out. */
export function clearSession(store: Store | undefined = storage()): void {
  try {
    store?.removeItem(SESSION);
  } catch {
    // nothing to forget
  }
}

/**
 * PENDING_LIFE bounds a sign-in under way: thirty minutes from the redirect
 * out — room for a registration at the provider with an email round trip,
 * which ten minutes was not.
 */
export const PENDING_LIFE = 30 * 60 * 1000;

/** savePending keeps a sign-in under way, keyed by its state. */
export function savePending(p: Pending, store: Store | undefined = pendingStorage()): void {
  write(store, PENDING + p.state, p);
}

/** takePending reads and forgets a sign-in under way by its state; a stale or unknown one is nothing. */
export function takePending(state: string, store: Store | undefined = pendingStorage()): Pending | undefined {
  const p = read(store, PENDING + state) as Pending | undefined;
  try {
    store?.removeItem(PENDING + state);
  } catch {
    // nothing to forget
  }
  if (p === undefined || p.state !== state) {
    return undefined;
  }
  if (Date.now() - p.begun > PENDING_LIFE) {
    return undefined;
  }
  return p;
}

/** saveProof keeps a proving sign-in for the Sign-in tab, beside the session, in this tab. */
export function saveProof(p: Proof, store: Store | undefined = storage()): void {
  write(store, PROOF, p);
}

/** takeProof reads and forgets the proving sign-in, or nothing. */
export function takeProof(store: Store | undefined = storage()): Proof | undefined {
  const raw = read(store, PROOF) as Partial<Proof> | undefined;
  try {
    store?.removeItem(PROOF);
  } catch {
    // nothing to forget
  }
  const probe: unknown = raw?.probe;
  const session = raw?.session;
  if (
    (probe !== "own" && probe !== "inherited") ||
    session === undefined ||
    typeof session.credential !== "string"
  ) {
    return undefined;
  }
  return { probe, session };
}

/** hasProof reports whether a proving sign-in waits, without taking it. */
export function hasProof(store: Store | undefined = storage()): boolean {
  return read(store, PROOF) !== undefined;
}
